import { useRef } from 'react';
import { makeStyles, DefaultTheme } from '@material-ui/styles';
import { Text, Theme } from '@bizzabo/design-system';
import React from 'react';
import cn from 'clsx';
import { User } from '@bizzabo/entities';
import Avatar from '../Avatar';

const generateName = (
    firstName = ' ',
    admin: boolean,
    moderatorName: string,
    lastName: string
): string => {
    const unnamedTitle = !(firstName || lastName) && 'Unnamed';
    const adminTitle = admin && moderatorName;
    const defaultTitle = (firstName && `${firstName} ${lastName}`) || `${lastName}`;

    return adminTitle || unnamedTitle || defaultTitle;
};

const anonymousAvatarEmoji =
    'https://bizzabo-demo-res.cloudinary.com/image/upload/v1636963257/ring/robot_eti6t7.png';
const adminAvatarEmoji =
    'https://bizzabo-demo-res.cloudinary.com/image/upload/v1636963257/ring/megaphone_wf6eke.png';

export interface UserBadgeProps {
    user: User;
    sizeOfAvatar?: number;
    titleVariant?: keyof DefaultTheme['typography'];
    descriptionVariant?: keyof DefaultTheme['typography'];
    badge?: JSX.Element;
    isTyping?: boolean;
    showUserPopup?: (params: {
        id: number;
        avatarRef: React.MutableRefObject<HTMLDivElement>;
    }) => Promise<void>;
    nameSibling?: JSX.Element;
    nameSiblingWithWrap?: boolean;
    isBadgeClickable?: boolean;
    className?: string;
}

type StyleProps = {
    hasNameSibling: boolean;
    nameSiblingWithWrap: boolean;
};

const UserBadge: React.FC<UserBadgeProps> = ({
    user,
    sizeOfAvatar,
    titleVariant = 't71',
    descriptionVariant = 't81',
    badge,
    isTyping = false,
    showUserPopup,
    nameSibling,
    nameSiblingWithWrap = false,
    isBadgeClickable = false,
    className,
}): JSX.Element => {
    const hasNameSibling = !!nameSibling;
    const classes = useStyles({ hasNameSibling, nameSiblingWithWrap });

    const name = generateName(user.firstName, user.admin, user.moderatorName, user.lastName);

    const getUserDetails = (title = '', company: string): string => {
        let userDetails;

        if (company && title) {
            userDetails = `${title} @ ${company}`;
        } else {
            userDetails = company ? `@ ${company}` : title;
        }

        return userDetails;
    };

    const notEmbeddedMode = window.parent.location === window.location;

    const isUserPopupEnabled = !!(notEmbeddedMode && isBadgeClickable);

    const avatarRef = useRef<HTMLDivElement>();

    const userClick = () => {
        if (!isUserPopupEnabled) {
            return;
        }
        showUserPopup?.({ id: user.id, avatarRef });
    };

    const inputFooterNoCollapse = isUserPopupEnabled
        ? { 'data-nocollapse': 'profile-card-opener' }
        : {};

    const userNameClassName = cn(
        classes.userName,
        { [classes.userNameClickable]: isUserPopupEnabled },
        'user-name'
    );

    const avatarClassName = cn(classes.avatarBadge, isUserPopupEnabled && classes.cursorPointer);

    return (
        <div className={cn(classes.userBadge, 'user-badge', className)}>
            <div
                className={cn(classes.avatarContainer, 'avatar-container')}
                {...inputFooterNoCollapse}
            >
                {(user.admin || user.anonymous) && (
                    <Avatar
                        userClick={userClick}
                        className={classes.avatarBadge}
                        size={sizeOfAvatar}
                        src={user.anonymous ? anonymousAvatarEmoji : adminAvatarEmoji}
                        badge={badge}
                    />
                )}
                {!user.anonymous && !user.admin && (
                    <Avatar
                        userClick={userClick}
                        alt={name}
                        src={user.photoUrl}
                        firstName={user.firstName}
                        lastName={user.lastName}
                        className={avatarClassName}
                        ref={isUserPopupEnabled ? avatarRef : null}
                        size={sizeOfAvatar}
                        badge={badge}
                    />
                )}
            </div>
            <div className={classes.personInfoContainer}>
                {user.anonymous || user.admin ? (
                    <div className={classes.userNameWrapper}>
                        <Text
                            variant={titleVariant}
                            className={cn(classes.personName, 'person-name')}
                        >
                            {user.anonymous ? 'Anonymous' : name}
                        </Text>
                        {hasNameSibling && nameSibling}
                    </div>
                ) : (
                    <div className={classes.personInfo}>
                        <div className={classes.userNameWrapper}>
                            <Text
                                className={userNameClassName}
                                variant={titleVariant}
                                onClick={userClick}
                                nowrap
                                {...inputFooterNoCollapse}
                            >
                                {name}
                            </Text>
                            {hasNameSibling && nameSibling}
                        </div>
                        <Text
                            className={cn(classes.userInfo, 'user-info')}
                            variant={descriptionVariant}
                            nowrap
                        >
                            {isTyping ? 'Typing...' : getUserDetails(user.title, user.company)}
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );
};

export const useStyles = makeStyles<Theme, StyleProps>(
    theme => ({
        userBadge: {
            display: 'flex',
            width: `100%`,
            marginBottom: 8,
        },
        avatarContainer: {
            display: 'flex',
            alignItems: 'center',
            marginRight: 8,
        },
        avatarBadge: {
            ...theme.typography.t71,
        },
        personInfoContainer: {
            overflow: 'hidden',
            flex: 5,
            alignSelf: 'center',
            width: `calc(100% - 10px)`,
        },
        personInfo: {
            display: 'flex',
            flexDirection: 'column',
        },
        userNameWrapper: {
            display: 'flex',
            alignItems: 'flex-end',
            flexWrap: ({ nameSiblingWithWrap }) => (nameSiblingWithWrap ? 'wrap' : 'initial'),
        },
        personName: {
            color: theme.palette.greys[700],
            wordBreak: 'break-word',
        },
        userName: {
            maxWidth: 'fit-content',
            fontWeight: 600,
            marginRight: ({ hasNameSibling }) => (hasNameSibling ? 4 : 0),
            color: theme.palette.greys[700],
        },
        userInfo: {
            width: 'fit-content',
            color: theme.palette.greys[500],
            fontSize: '1.2rem',
        },
        cursorPointer: {
            cursor: 'pointer',
        },
        userNameClickable: {
            cursor: 'pointer',
            position: 'relative',
            '&:hover': {
                '&:after': {
                    content: '""',
                    position: 'absolute',
                    backgroundColor: theme.palette.greys[500],
                    height: 1,
                    left: 0,
                    right: 0,
                    bottom: 0,
                },
            },
        },
    }),
    { classNamePrefix: UserBadge.name }
);

export default UserBadge;
